import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Avatar,
  Box,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import { getIssuerBadgeProfile } from '../services/apiservice';
import { Helmet } from 'react-helmet-async';

const IssuerBadgePage = () => {
  const { profileId } = useParams();
  const [issuerData, setIssuerData] = useState(null);
  const [sortOrder, setSortOrder] = useState('Most Recent');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const response = await getIssuerBadgeProfile(profileId);
      if (response.success && response.message !== 'No user found with ProfileID') {
        setIssuerData(response.data);
      } else if (response.success && response.message == 'No user found with ProfileID') {
        navigate('*')
      }
    };

    fetchUserData();
  }, [profileId]);

  if (!issuerData) {
    return (
      <Container
        maxWidth={false}
        sx={{
          padding: '20px',
          background: 'linear-gradient(to right, #c3cfe2, #f5f7fa)',
          textAlign: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          overflowY: 'auto',
          zIndex: -1,
        }}
      >
        <Helmet>
          <title>CIRA - Badge Issuer Profile</title>
        </Helmet>
        <Box
          textAlign="center"
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Skeleton variant="circular" width={120} height={120} />
          <Skeleton variant="text" height={60} width="30%" />
          <Skeleton variant="text" height={30} width="30%" />

          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            mt={2}
            mb={3}
          >
            <Skeleton variant="text" height={40} width="15%" />
            <Skeleton variant="rectangular" width={120} height={40} />
          </Box>

          <Grid container spacing={2} justifyContent="center">
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ padding: 2, textAlign: 'center' }}>
                  <Skeleton
                    variant="circular"
                    width={60}
                    height={60}
                    sx={{ margin: '0 auto' }}
                  />
                  <Skeleton variant="text" height={30} width="80%" sx={{ mt: 1, mx: 'auto' }} />
                  <Skeleton variant="text" height={20} width="60%" sx={{ mb: 1, mx: 'auto' }} />
                  <Skeleton variant="text" height={20} width="60%" sx={{ mb: 1, mx: 'auto' }} />
                  <Skeleton
                    variant="rectangular"
                    height={20}
                    width="40%"
                    sx={{ margin: '0 auto' }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }

  const sortedBadges = Array.isArray(issuerData?.Badges) ?
    [...issuerData.Badges].sort((a, b) => {
      if (sortOrder === 'Most Recent') {
        const latestAssignmentA = a.Assignments.length > 0 ? new Date(Math.max(...a.Assignments.map(assignment => new Date(assignment.AssignmentDate)))) : 0;
        const latestAssignmentB = b.Assignments.length > 0 ? new Date(Math.max(...b.Assignments.map(assignment => new Date(assignment.AssignmentDate)))) : 0;
        return latestAssignmentB - latestAssignmentA;
      }
      return b.PopularityMetric - a.PopularityMetric;
    }) : [];

  return (
    <Container
      maxWidth={false}
      sx={{
        padding: '20px',
        background: 'linear-gradient(to top, #a7a6cb 0%, #8989ba 52%, #8989ba 100%)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        textAlign: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        overflowY: 'auto',
        zIndex: -1,
      }}
    >
      <Box textAlign="center" marginBottom={4} sx={{ backgroundImage: 'linear-gradient(to top, #a7a6cb 0%, #8989ba 52%, #8989ba 100%)', padding: '16px', borderRadius: '8px' }}>
        <Avatar
          sx={{ width: 120, height: 120, margin: '0 auto', border: '4px solid rgba(255, 255, 255, 0.8)', boxShadow: 2 }}
          src={issuerData.IssuerLogoURL}
          alt={issuerData.IssuerName}
        />
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#fff' }}>
          {issuerData.IssuerName}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" sx={{ color: '#fff' }}>
          {issuerData.IssuerWebsite}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Typography variant="h6" sx={{ textAlign: 'left', color: '#333' }}>
          {issuerData.Badges.length} Badge{issuerData.Badges.length !== 1 ? 's' : ''} Issued
        </Typography>
        <FormControl variant="outlined" sx={{ minWidth: 120 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            label="Sort By"
          >
            <MenuItem value="Most Recent">Most Recent</MenuItem>
            <MenuItem value="Most Popular">Most Popular</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {sortedBadges.map((badge) => (
          <Grid item xs={12} sm={6} md={4} key={badge.CredentialID}>
            <Card
              sx={{
                padding: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #dfe9f3 0%, #c1d7e9 100%)',
                borderRadius: '16px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
                  '& .credential-name': {
                    transform: 'scale(1.1)',
                  },
                },
              }}
            >

              <Avatar
                sx={{
                  width: 70,
                  height: 70,
                  margin: '0 auto',
                  transition: 'transform 0.3s ease-in-out',
                  border: '2px solid #fff',
                }}
                src={badge.CredentialImageURL}
                alt={badge.CredentialName}
              />
              <CardContent>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: '8px',
                    transition: 'transform 0.3s ease-in-out',
                  }}
                  className="credential-name"
                >
                  {badge.CredentialName}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    color: '#555',
                    lineHeight: 1.5,
                  }}
                >
                  {badge.CredentialDescription}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" sx={{ mt: 1 }}>
                  <PeopleIcon color="primary" />
                  <Typography
                    variant="body2"
                    color="primary"
                    fontWeight="bold"
                    sx={{ ml: 0.5, color: '#1a73e8' }}
                  >
                    Recipients: {badge.Assignments.length}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default IssuerBadgePage;